import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

function ChangeOwnerName() {
    const location = useLocation();
    const { data } = location.state;
    const [userlistData, setUserListData] = useState([]);
    const [formData, setFormData] = useState({
        product_id: data?.id || "",
        new_user_id: ""
    });
    
    // Searchable dropdown states
    const [searchQuery, setSearchQuery] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const fetchAllUserLists = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASEURL}/all-user-list/`,
                {
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
                    }
                }
            );
            setUserListData(response?.data);
        } catch (error) {
            console.error(error);
        }
    };

    const ChangeProductOwner = async () => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/product/change-owner/`,
                formData,
                {
                    headers: {
                        'accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
                    }
                }
            );
            toast.success(response.data.message);
            // Reset form after success
            setSelectedUser(null);
            setSearchQuery("");
            setFormData({
                ...formData,
                new_user_id: ""
            });
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Failed to change owner");
        }
    };

    // Filter users based on search query (username, email, phone)
    const filteredUsers = userlistData.filter((user) => {
        const query = searchQuery.toLowerCase();
        return (
            user?.username?.toLowerCase().includes(query) ||
            user?.email?.toLowerCase().includes(query) ||
            user?.phone?.toLowerCase().includes(query) ||
            user?.mobile?.toLowerCase().includes(query) // in case phone field is named 'mobile'
        );
    });

    // Handle user selection
    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setSearchQuery(`${user?.username} | ${user?.email} | ${user?.phone || user?.mobile || 'N/A'}`);
        setFormData({
            ...formData,
            new_user_id: user.uid
        });
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
    };

    // Handle input focus
    const handleInputFocus = () => {
        setIsDropdownOpen(true);
    };

    // Handle input change
    const handleInputChange = (e) => {
        setSearchQuery(e.target.value);
        setIsDropdownOpen(true);
        setHighlightedIndex(-1);
        // Clear selection if user modifies search
        if (selectedUser) {
            setSelectedUser(null);
            setFormData({
                ...formData,
                new_user_id: ""
            });
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isDropdownOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => 
                    prev < filteredUsers.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredUsers[highlightedIndex]) {
                    handleSelectUser(filteredUsers[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsDropdownOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0) {
            const highlightedElement = document.getElementById(`user-option-${highlightedIndex}`);
            if (highlightedElement) {
                highlightedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [highlightedIndex]);

    useEffect(() => {
        fetchAllUserLists();
    }, []);

    return (
        <>
            <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6 sm:p-8">
                    {/* Header Section */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Change Product Owner
                        </h2>
                        <p className="text-sm text-gray-600">
                            Transfer ownership of this product to another user
                        </p>
                    </div>

                    {/* Product Information Card */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h3 className="text-sm font-semibold text-blue-900 mb-3">
                            Product Details
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <p className="text-xs text-blue-600 font-medium mb-1">
                                    Product ID
                                </p>
                                <p className="text-sm text-gray-900 font-semibold">
                                    {data?.id}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-blue-600 font-medium mb-1">
                                    Equipment Name
                                </p>
                                <p className="text-sm text-gray-900 font-semibold">
                                    {data?.equip_name}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form Section */}
                    <div className="space-y-6">
                        <div ref={dropdownRef}>
                            <label
                                htmlFor="user-search"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Search & Select New Owner
                            </label>
                            
                            {/* Searchable Input */}
                            <div className="relative">
                                <input
                                    ref={inputRef}
                                    id="user-search"
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleInputChange}
                                    onFocus={handleInputFocus}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Search by name, email, or phone number..."
                                    className="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block px-4 py-3 pr-10 transition duration-200 ease-in-out hover:border-gray-400"
                                    autoComplete="off"
                                />
                                
                                {/* Search Icon */}
                                <svg
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                                    />
                                </svg>

                                {/* Dropdown List */}
                                {isDropdownOpen && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {filteredUsers.length > 0 ? (
                                            <ul className="py-1">
                                                {filteredUsers.map((user, index) => (
                                                    // console.log(user),
                                                    <li
                                                        key={user.uid}
                                                        id={`user-option-${index}`}
                                                        onClick={() => handleSelectUser(user)}
                                                        className={`px-4 py-3 cursor-pointer transition-colors ${
                                                            highlightedIndex === index
                                                                ? 'bg-blue-100'
                                                                : 'hover:bg-gray-100'
                                                        } ${
                                                            selectedUser?.uid === user.uid
                                                                ? 'bg-blue-50'
                                                                : ''
                                                        }`}
                                                    >
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {user?.username}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                {user?.email}
                                                            </span>
                                                            {(user?.phone || user?.mobile) && (
                                                                <span className="text-xs text-blue-600 font-medium mt-1">
                                                                    📞 {user?.phone || user?.mobile}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="px-4 py-8 text-center">
                                                <svg
                                                    className="mx-auto h-12 w-12 text-gray-400"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <p className="mt-2 text-sm text-gray-500">
                                                    No users found matching "{searchQuery}"
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* <p className="mt-2 text-xs text-gray-500">
                                <span className="font-medium">💡 Tip:</span> Search by username, email, or phone number. Use arrow keys to navigate.
                            </p> */}

                            {/* Selected User Display */}
                            {selectedUser && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-xs text-green-700 font-semibold mb-1">
                                                Selected User
                                            </p>
                                            <p className="text-sm text-gray-900 font-medium">
                                                {selectedUser?.username}
                                            </p>
                                            <p className="text-xs text-gray-600">
                                                {selectedUser?.email}
                                            </p>
                                            {(selectedUser?.phone || selectedUser?.mobile) && (
                                                <p className="text-xs text-gray-600">
                                                    📞 {selectedUser?.phone || selectedUser?.mobile}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => {
                                                setSelectedUser(null);
                                                setSearchQuery("");
                                                setFormData({
                                                    ...formData,
                                                    new_user_id: ""
                                                });
                                            }}
                                            className="text-green-600 hover:text-green-800 font-medium text-xs"
                                        >
                                            ✕ Clear
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={ChangeProductOwner}
                                disabled={!formData.new_user_id}
                                className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 shadow-sm"
                            >
                                Change Owner
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChangeOwnerName;



// import axios from 'axios';
// import React, { useEffect, useState } from 'react';
// import { useLocation } from 'react-router-dom';
// import { toast } from 'react-toastify';

// function ChangeOwnerName() {
//     const location = useLocation();
//     const { data } = location.state;
//     const [userlistData, setUserListData] = useState([]);
//     const [formData, setFormData] = useState({
//         product_id: data?.id || "",
//         new_user_id: ""
//     });

//     const fetchAllUserLists = async () => {
//         try {
//             const response = await axios.get(`${process.env.REACT_APP_BASEURL}/all-user-list/`,
//                 {
//                     headers: {
//                         'accept': 'application/json',
//                         'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
//                     }
//                 }
//             );
//             setUserListData(response?.data);
//         } catch (error) {
//             console.error(error);
//         }
//     };

//     const ChangeProductOwner = async () => {
//         try {
//             const response = await axios.post(`${process.env.REACT_APP_BASEURL}/product/change-owner/`,
//                 formData,
//                 {
//                     headers: {
//                         'accept': 'application/json',
//                         'Content-Type': 'application/json',
//                         'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
//                     }
//                 }
//             );
//             // console.log('Owner changed:', response.data);
//             toast.success(response.data.message)
//         } catch (error) {
//             console.error(error);
//         }
//     };

//     const handleUserChange = (e) => {
//         setFormData({
//             ...formData,
//             new_user_id: e.target.value
//         });
//     };

//     useEffect(() => {
//         fetchAllUserLists();
//     }, []);

  
//     return (
//         <>
//             <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//                 <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6 sm:p-8">
//                     {/* Header Section */}
//                     <div className="mb-8">
//                         <h2 className="text-2xl font-bold text-gray-900 mb-2">
//                             Change Product Owner
//                         </h2>
//                         <p className="text-sm text-gray-600">
//                             Transfer ownership of this product to another user
//                         </p>
//                     </div>

//                     {/* Product Information Card */}
//                     <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
//                         <h3 className="text-sm font-semibold text-blue-900 mb-3">
//                             Product Details
//                         </h3>
//                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                             <div>
//                                 <p className="text-xs text-blue-600 font-medium mb-1">
//                                     Product ID
//                                 </p>
//                                 <p className="text-sm text-gray-900 font-semibold">
//                                     {data?.id}
//                                 </p>
//                             </div>
//                             <div>
//                                 <p className="text-xs text-blue-600 font-medium mb-1">
//                                     Equipment Name
//                                 </p>
//                                 <p className="text-sm text-gray-900 font-semibold">
//                                     {data?.equip_name}
//                                 </p>
//                             </div>
//                         </div>
//                     </div>

//                     {/* Form Section */}
//                     <div className="space-y-6">
//                         <div>
//                             <label
//                                 htmlFor="user-select"
//                                 className="block text-sm font-medium text-gray-700 mb-2"
//                             >
//                                 Select New Owner
//                             </label>
//                             <div className="relative">
//                                 <select
//                                     id="user-select"
//                                     value={formData.new_user_id}
//                                     onChange={handleUserChange}
//                                     className="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block px-4 py-3 appearance-none cursor-pointer transition duration-200 ease-in-out hover:border-gray-400"
//                                 >
//                                     <option value="">Select a User </option>
//                                     {userlistData.map((item) => (
//                                         <option key={item.uid} value={item.uid}>
//                                             {item?.username} | {item?.email}
//                                         </option>
//                                     ))}
//                                 </select>
//                                 <svg
//                                     className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
//                                     xmlns="http://www.w3.org/2000/svg"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                     strokeWidth="1.5"
//                                     stroke="currentColor"
//                                 >
//                                     <path
//                                         strokeLinecap="round"
//                                         strokeLinejoin="round"
//                                         d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
//                                     />
//                                 </svg>
//                             </div>
//                             <p className="mt-2 text-xs text-gray-500">
//                                 Choose a user to transfer ownership of this product
//                             </p>
//                         </div>

//                         {/* Action Buttons */}
//                         <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
//                             <button
//                                 type="button"
//                                 onClick={() => window.history.back()}
//                                 className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={ChangeProductOwner}
//                                 disabled={!formData.new_user_id}
//                                 className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 shadow-sm"
//                             >
//                                 Change Owner
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </>

//     );
// }

// export default ChangeOwnerName;
